import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiHome, FiActivity, FiBookOpen, FiCalendar, FiX, FiMenu, FiEdit3 } from 'react-icons/fi';
import logo from '../assets/images/logo1.png';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleScrollToSection = (sectionId) => {
    setMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      navigate('/', {
        state: { scrollTo: sectionId }
      });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className="relative z-50 bg-white shadow-sm">
      <div className="max-w-8xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo with image */}
          <Link to="/" className="flex items-center">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2"
            >
              {/* Logo image */}
              <img 
                src={logo} 
                alt="FlowHearts Logo" 
                className="h-auto w-[100px] object-contain" // Adjust size as needed
              />
              {/* Logo text */}
              <span className="text-4xl md:text-5xl font-dancing font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">
                FlowHearts
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            <button
              onClick={() => handleScrollToSection('home-section')}
              className={`flex items-center gap-2 font-nunito text-lg px-4 py-2 rounded-full transition-all ${
                location.pathname === '/' 
                  ? 'bg-pink-100 text-purple-600 font-semibold shadow-inner' 
                  : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
              }`}
            >
              <FiHome className="text-xl" />
              <span>Home</span>
            </button>

            <button
              onClick={() => handleScrollToSection('tools-section')}
              className={`flex items-center gap-2 font-nunito text-lg px-4 py-2 rounded-full transition-all ${
                location.pathname === '/' 
                  ? 'text-purple-800 hover:bg-pink-50 hover:shadow-sm' 
                  : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
              }`}
            >
              <FiActivity className="text-xl" />
              <span>Services</span>
            </button>

            <button
              onClick={() => handleScrollToSection('wellness-section')}
              className={`flex items-center gap-2 font-nunito text-lg px-4 py-2 rounded-full transition-all ${
                location.pathname === '/' 
                  ? 'text-purple-800 hover:bg-pink-50 hover:shadow-sm' 
                  : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
              }`}
            >
              <FiBookOpen className="text-xl" />
              <span>Tips & Care</span>
            </button>

            <Link
              to="/period-tracker"
              className={`flex items-center gap-2 font-nunito text-lg px-4 py-2 rounded-full transition-all ${
                location.pathname.startsWith('/period-tracker') 
                  ? 'bg-pink-100 text-purple-600 font-semibold shadow-inner' 
                  : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
              }`}
            >
              <FiCalendar className="text-xl" />
              <span>Track days</span>
            </Link>

            {/* Blog Link with Animated Gradient */}
            <Link
              to="/blog"
              className={`flex items-center gap-2 font-nunito text-lg px-4 py-2 rounded-full transition-all ${
                location.pathname.startsWith('/blog') 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold shadow-md' 
                  : 'bg-gradient-to-r hover:from-pink-400 hover:to-purple-500 from-pink-300 to-purple-400 text-white font-medium shadow-sm'
              }`}
            >
              <FiEdit3 className="text-xl" />
              <motion.span
                animate={{
                  backgroundPosition: ['0%', '100%', '0%'],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'linear',
                }}
                className="bg-clip-text text-transparent bg-gradient-to-r from-white via-pink-100 to-white"
              >
                Blog
              </motion.span>
            </Link>
          </nav>

          <button 
            className="md:hidden text-purple-800 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <FiX className="w-8 h-8" />
            ) : (
              <FiMenu className="w-8 h-8" />
            )}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden bg-white rounded-lg shadow-lg mx-2 mb-4"
            >
              <div className="flex flex-col space-y-2 p-4">
                <button
                  onClick={() => handleScrollToSection('home-section')}
                  className={`flex items-center gap-3 font-nunito text-xl px-6 py-3 rounded-full transition-all ${
                    location.pathname === '/' 
                      ? 'bg-pink-100 text-purple-600 font-semibold shadow-inner' 
                      : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
                  }`}
                >
                  <FiHome className="text-xl" />
                  <span>Home</span>
                </button>

                <button
                  onClick={() => handleScrollToSection('tools-section')}
                  className={`flex items-center gap-3 font-nunito text-xl px-6 py-3 rounded-full transition-all ${
                    location.pathname === '/' 
                      ? 'text-purple-800 hover:bg-pink-50 hover:shadow-sm' 
                      : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
                  }`}
                >
                  <FiActivity className="text-xl" />
                  <span>Services</span>
                </button>

                <button
                  onClick={() => handleScrollToSection('wellness-section')}
                  className={`flex items-center gap-3 font-nunito text-xl px-6 py-3 rounded-full transition-all ${
                    location.pathname === '/' 
                      ? 'text-purple-800 hover:bg-pink-50 hover:shadow-sm' 
                      : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
                  }`}
                >
                  <FiBookOpen className="text-xl" />
                  <span>Tips & Care</span>
                </button>

                <Link
                  to="/period-tracker"
                  className={`flex items-center gap-3 font-nunito text-xl px-6 py-3 rounded-full transition-all ${
                    location.pathname.startsWith('/period-tracker') 
                      ? 'bg-pink-100 text-purple-600 font-semibold shadow-inner' 
                      : 'text-purple-800 hover:bg-pink-50 hover:shadow-sm'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FiCalendar className="text-xl" />
                  <span>Track my days</span>
                </Link>

                {/* Mobile Blog Link with Animated Gradient */}
                <Link
                  to="/blog"
                  className={`flex items-center gap-3 font-nunito text-xl px-6 py-3 rounded-full transition-all ${
                    location.pathname.startsWith('/blog') 
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold shadow-md' 
                      : 'bg-gradient-to-r hover:from-pink-400 hover:to-purple-500 from-pink-300 to-purple-400 text-white font-medium shadow-sm'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FiEdit3 className="text-xl" />
                  <motion.span
                    animate={{
                      backgroundPosition: ['0%', '100%', '0%'],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                    className="bg-clip-text text-transparent bg-gradient-to-r from-white via-pink-100 to-white"
                  >
                    Blog
                  </motion.span>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default Header;